<script lang="ts" setup>
// Родной компонент DataTable слишком косячный.
// Данный компонент призван убрать проблемы с рендером

import { ref, computed } from 'vue'
import type { TableColumn } from '@/components/alerts/types'
import get from 'lodash/get'
import Paginator from 'primevue/paginator'

const props = defineProps<{
  size: 'small' | 'medium' | 'large'
  totalRecords?: number
  fakeRowsCount?: number
  rows?: number
  lazy?: boolean
  paginator?: boolean
  loading?: boolean
  formattingRules?: Record<
    string,
    (v: any, ctx?: Record<string, any>, field_name?: string) => any // eslint-disable-line
  >
  skeletonHeight?: string
  value?: unknown[]
  colFull?: boolean
  columns: TableColumn[]
}>()

const formattingRules = computed(() => {
  return props.formattingRules ?? {}
})

const sortField = defineModel<string>('sortField')
const sortOrder = defineModel<number>('sortOrder')

const value = computed(() => {
  if (!props.lazy && sortField.value) {
    const arr = props.value?.slice() as any[]  // eslint-disable-line
    arr.sort((a, b) => {
      if (sortField.value) {
        if (sortOrder.value == -1) {
          return (a?.[sortField.value] ?? 1) > (b?.[sortField.value] ?? 1) ? 1 : -1
        } else {
          return (a?.[sortField.value] ?? 1) < (b?.[sortField.value] ?? 1) ? 1 : -1
        }
      }
      return 1
    })
    return arr
  } else {
    return props.value as any[]  // eslint-disable-line
  }
})

const showSkeletons = computed(() => {
  const data = value.value
  return (data?.length ?? 0) == 0
})

// Размер текста
const textSizeClasses = {
  'small': 'text-sm',
  'medium': 'text-md',
  'large': 'text-lg',
}
const textSizeClass = computed(() => textSizeClasses[props.size])
const currentPage = ref<number>(0)

const page = (page_num: number) => {
  if (props.loading || page_num == currentPage.value) {
    return
  }
  emit('page', page_num, sortField.value ?? null, sortOrder.value ?? null)
  currentPage.value = page_num
}

const emit = defineEmits<{
  (e: 'page', page: number, field: string | null, order: number | null): void
  (e: 'sort'): void
}>()

const callRule = (field: string, data: Record<string, unknown>) => {
  if (!formattingRules.value || !data || !field) {
    return
  }
  const fieldValue = get(data, field) as string
  const rules = formattingRules.value
  if (field in rules) {
    return rules[field]?.(fieldValue, data, field)
  }
  return fieldValue
}

const onSort = (fieldName: string) => {
  if (sortField.value == fieldName) {
    sortOrder.value = sortOrder.value == 1 ? -1 : 1
  } else {
    sortField.value = fieldName
    sortOrder.value = -1
  }
  emit('sort')
}

// const getClasses = (classes: string | null | undefined) => {
//   if (!classes) {
//     classes = ''
//   }
//   if (!classes?.includes('w-') && props.colFull) {
//     classes += ' w-full'
//   }
//   return classes
// }

const genClass = (col: TableColumn, forceNp = false) => {
  const noPadding = (col.noPadding ?? false) || forceNp
  return [
    { "sr-table-body-column-content": !noPadding },
    col.class,
    textSizeClass.value
  ]
}
</script>

<template>
  <div class="sr-table-wrapper">

    <table class="sr-table sr-data-table table-fixed" :class="{ 'table-loading': props.loading }">
      <thead class="sr-table-thead">
        <!-- Оглавление -->
        <tr>
          <th class="sr-table-header-cell" v-for="col in props.columns" :key="col.field" :class="genClass(col, true)"
            :style="col.width ? { width: col.width } : undefined">
            <div :class="{ 'cursor-pointer': col.sortable }" @click="col.sortable ? onSort(col.field) : undefined">
              <span class="sr-table-header-column-title">
                {{ col.header ? $t(col.header) : '' }}
              </span>
              <span v-if="col.sortable">
                <i style="font-size: 10px;" class="ms-2 pi" :class="{
                  'pi-sort-up-fill': sortField == col.field && sortOrder == -1,
                  'pi-sort-down-fill': !(sortField == col.field && sortOrder == -1),
                }"></i>
              </span>
            </div>
          </th>
        </tr>
      </thead>
      <tbody class="st-table-tbody">

        <!-- Скелетоны -->
        <template v-if="showSkeletons && loading">
          <tr v-for="idx in [...new Array(props.fakeRowsCount ?? 15).keys()]" :key="idx">
            <td :class="genClass(col)" v-for="col in props.columns" :key="col.field"
              :style="col.width ? { width: col.width } : undefined">
              <div :style="`height: ${props.skeletonHeight};width: ${(col.skeletonWidth ?? '100px')}`"
                class="skeleton-bar max-w-full"></div>
            </td>
          </tr>
        </template>

        <!-- Пустая таблица -->
        <tr v-if="value?.length == 0 && !loading">
          <td class="sr-table-body-column-content" :colspan="props.columns.length">
            <slot name="empty"></slot>
          </td>
        </tr>

        <!-- Данные -->
        <tr v-for="(data, idx) in value" :key="data?.id ?? idx">
          <template v-if="data">
            <td v-for="col in props.columns" :key="col.field" :class="`${textSizeClass} ${col.class}`"
              class="sr-table-body-column-content" :style="col.width ? { width: col.width } : undefined">
              <component v-bind="col.dynamic && col.innerPropsGenerator && data
                ? col.innerPropsGenerator(data)
                : col.innerProps
                " :is="col.inner ?? 'span'">
                {{
                  col.dynamic && col.innerDataGenerator && data
                    ? col.innerDataGenerator(data)
                    : callRule(col.field, data)
                }}
              </component>
            </td>
          </template>
          <template v-else>
            <td></td>
          </template>
        </tr>
      </tbody>
    </table>
  </div>
  <Paginator v-if="props.paginator ?? false" :rows="props.rows" @page="page($event.page)"
    :totalRecords="props.totalRecords ? props.totalRecords : props.rows">
  </Paginator>
</template>

<style scoped>
.sr-data-table :deep(td),
.sr-data-table :deep(th) {
  padding: 10px;
}
</style>
